import { useState } from 'react';
import _ from 'lodash';
import { type BoardSource, BoardSourceSchema } from '@gorilla/portal-shared/src/schemas';
import { Flex, Box, Text, Icon, Button, Heading } from '@vibe/core';
import { AppModal, AppModalContent } from '@gorilla/ui/src/components/AppModal';
import { Settings } from '@vibe/icons';
import { boardSourceDescriptorsByType, BoardSourceIndicator } from './board-sources/index';

type EditBoardSourceModalProps = {
  boardSource: BoardSource;
  onClose: (boardSource?: BoardSource) => void;
};

export function EditBoardSourceModal({ boardSource, onClose }: EditBoardSourceModalProps) {
  const boardSourceDescriptor = boardSourceDescriptorsByType[boardSource.type];

  const [boardSourceDraft, setBoardSourceDraft] = useState<Partial<BoardSource> | null>(() => {
    return _.cloneDeep(boardSource) as BoardSource;
  });

  const boardSourceDraftResult = BoardSourceSchema.safeParse(boardSourceDraft);
  const BoardSourceComponent = boardSourceDescriptor.component;

  if (!BoardSourceComponent) {
    return <div>Unsupported board source type.</div>;
  }

  const content = (
    <Box>
      <Flex direction="row" align="center" gap={8} style={{ marginBottom: '24px' }}>
        <Icon icon={Settings} size={24} style={{ transform: 'translate3d(0, -1px, 0)' }} />
        <Heading type="h3" weight="bold" align="start">
          {boardSource.name}
        </Heading>
      </Flex>
      <BoardSourceComponent
        // The descriptor lookup yields a union of components, so TS intersects their `source` props.
        source={boardSourceDraft as any}
        onChange={(source) => {
          setBoardSourceDraft(source);
        }}
        mode="edit"
      />
      <Flex gap={16} style={{ marginBottom: '10px' }}>
        <Box
          style={{
            flex: 1,
          }}
        >
          <Flex gap={24}>
            <Text type="text2" align="start" color="primary" ellipsis element="p">
              <BoardSourceIndicator boardSourceType={boardSourceDescriptor.type}>{boardSourceDescriptor.description}</BoardSourceIndicator>
            </Text>
          </Flex>
        </Box>
        <Box marginEnd="auto">
          <Flex gap={10}>
            <Button
              kind="tertiary"
              size="medium"
              onClick={() => {
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              kind="primary"
              size="medium"
              disabled={boardSourceDraftResult.success === false || !boardSourceDraft?.name}
              onClick={() => {
                if (boardSourceDraft) {
                  onClose(boardSourceDraft as BoardSource);
                } else {
                  onClose();
                }
              }}
            >
              Save
            </Button>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );

  return (
    <AppModal
      show={true}
      onClose={() => {
        onClose();
      }}
      contentSpacing={false}
      alertDialog={true}
      width={800}
      id="board-source-create__modal"
    >
      <AppModalContent>{content!}</AppModalContent>
    </AppModal>
  );
}
